import { useCallback } from 'react';

// Conversion categories and units with full names
const CATEGORIES = {
  length: {
    name: "Length",
    base: "m",
    units: {
      "m": { factor: 1, fullName: "Meter" },
      "km": { factor: 1000, fullName: "Kilometer" },
      "cm": { factor: 0.01, fullName: "Centimeter" },
      "mm": { factor: 0.001, fullName: "Millimeter" },
      "µm": { factor: 1e-6, fullName: "Micrometer" },
      "nm": { factor: 1e-9, fullName: "Nanometer" },
      "mi": { factor: 1609.344, fullName: "Mile" },
      "yd": { factor: 0.9144, fullName: "Yard" },
      "ft": { factor: 0.3048, fullName: "Foot" },
      "in": { factor: 0.0254, fullName: "Inch" },
      "nmi": { factor: 1852, fullName: "Nautical Mile" }
    }
  },
  mass: {
    name: "Mass",
    base: "kg",
    units: {
      "kg": { factor: 1, fullName: "Kilogram" },
      "g": { factor: 0.001, fullName: "Gram" },
      "mg": { factor: 1e-6, fullName: "Milligram" },
      "t": { factor: 1000, fullName: "Metric Ton" },
      "lb": { factor: 0.45359237, fullName: "Pound" },
      "oz": { factor: 0.028349523125, fullName: "Ounce" },
      "st": { factor: 6.35029318, fullName: "Stone" }
    }
  },
  time: {
    name: "Time",
    base: "s",
    units: {
      "s": { factor: 1, fullName: "Second" },
      "ms": { factor: 0.001, fullName: "Millisecond" },
      "min": { factor: 60, fullName: "Minute" },
      "h": { factor: 3600, fullName: "Hour" },
      "day": { factor: 86400, fullName: "Day" },
      "week": { factor: 604800, fullName: "Week" },
      "year": { factor: 31557600, fullName: "Year" }
    }
  },
  temperature: {
    name: "Temperature",
    base: "K",
    units: {
      "C": {
        toBase: (v) => v + 273.15,
        fromBase: (k) => k - 273.15,
        fullName: "Celsius"
      },
      "F": {
        toBase: (v) => (v - 32) * 5/9 + 273.15,
        fromBase: (k) => (k - 273.15) * 9/5 + 32,
        fullName: "Fahrenheit"
      },
      "K": {
        toBase: (v) => v,
        fromBase: (k) => k,
        fullName: "Kelvin"
      }
    }
  },
  area: {
    name: "Area",
    base: "m2",
    units: {
      "m2": { factor: 1, fullName: "Square Meter" },
      "km2": { factor: 1e6, fullName: "Square Kilometer" },
      "cm2": { factor: 0.0001, fullName: "Square Centimeter" },
      "mm2": { factor: 1e-6, fullName: "Square Millimeter" },
      "ha": { factor: 10000, fullName: "Hectare" },
      "acre": { factor: 4046.8564224, fullName: "Acre" },
      "mi2": { factor: 2589988.110336, fullName: "Square Mile" },
      "ft2": { factor: 0.09290304, fullName: "Square Foot" },
      "in2": { factor: 0.00064516, fullName: "Square Inch" }
    }
  },
  volume: {
    name: "Volume",
    base: "L",
    units: {
      "L": { factor: 1, fullName: "Liter" },
      "mL": { factor: 0.001, fullName: "Milliliter" },
      "m3": { factor: 1000, fullName: "Cubic Meter" },
      "cm3": { factor: 0.001, fullName: "Cubic Centimeter" },
      "ft3": { factor: 28.316846592, fullName: "Cubic Foot" },
      "in3": { factor: 0.016387064, fullName: "Cubic Inch" },
      "gal": { factor: 3.785411784, fullName: "US Gallon" },
      "qt": { factor: 0.946352946, fullName: "US Quart" },
      "pt": { factor: 0.473176473, fullName: "US Pint" },
      "cup": { factor: 0.24, fullName: "US Cup" },
      "floz": { factor: 0.0295735295625, fullName: "US Fluid Ounce" }
    }
  },
  speed: {
    name: "Speed",
    base: "m/s",
    units: {
      "m/s": { factor: 1, fullName: "Meter per Second" },
      "km/h": { factor: 0.277778, fullName: "Kilometer per Hour" },
      "mph": { factor: 0.44704, fullName: "Mile per Hour" },
      "ft/s": { factor: 0.3048, fullName: "Foot per Second" },
      "kn": { factor: 0.514444, fullName: "Knot" }
    }
  },
  data: {
    name: "Data Storage",
    base: "B",
    units: {
      "B": { factor: 1, fullName: "Byte" },
      "KB": { factor: 1000, fullName: "Kilobyte" },
      "MB": { factor: 1e6, fullName: "Megabyte" },
      "GB": { factor: 1e9, fullName: "Gigabyte" },
      "TB": { factor: 1e12, fullName: "Terabyte" },
      "PB": { factor: 1e15, fullName: "Petabyte" },
      "KiB": { factor: 1024, fullName: "Kibibyte" },
      "MiB": { factor: 1048576, fullName: "Mebibyte" },
      "GiB": { factor: 1073741824, fullName: "Gibibyte" },
      "TiB": { factor: 1099511627776, fullName: "Tebibyte" }
    }
  }
};

// Find category for a unit
export function findCategory(unit) {
  for (const [cat, info] of Object.entries(CATEGORIES)) {
    if (cat === "temperature") {
      if (info.units[unit]) return cat;
    } else {
      if (info.units[unit]) return cat;
    }
  }
  return null;
}

// Main conversion function
export function convert(value, from, to) {
  const catFrom = findCategory(from);
  const catTo = findCategory(to);
  
  if (!catFrom || !catTo || catFrom !== catTo) {
    throw new Error("Units must be in same category or recognized.");
  }
  
  if (catFrom === "temperature") {
    const uFrom = CATEGORIES.temperature.units[from];
    const uTo = CATEGORIES.temperature.units[to];
    const base = uFrom.toBase(Number(value));
    return uTo.fromBase(base);
  } else {
    const cat = CATEGORIES[catFrom];
    const factorFrom = cat.units[from].factor;
    const factorTo = cat.units[to].factor;
    const valueInBase = Number(value) * factorFrom;
    return valueInBase / factorTo;
  }
}

// Format number for display
export function formatResult(num) {
  // Handle very large or very small numbers with scientific notation
  if (Math.abs(num) > 1e10 || (Math.abs(num) < 1e-6 && num !== 0)) {
    return num.toExponential(6);
  }
  
  // For regular numbers, show up to 8 significant digits
  return parseFloat(num.toPrecision(8)).toString();
}

// Custom hook for converter
export function useConverter() {
  const getUnitsForCategory = useCallback((category) => {
    return CATEGORIES[category]?.units || {};
  }, []);

  const getCategoryName = useCallback((category) => {
    return CATEGORIES[category]?.name || category;
  }, []);

  const getAllCategories = useCallback(() => {
    return CATEGORIES;
  }, []);

  const getUnitFullName = useCallback((category, unit) => {
    const units = CATEGORIES[category]?.units;
    return units && units[unit] ? units[unit].fullName : unit;
  }, []);

  return {
    convert,
    formatResult,
    findCategory,
    getUnitsForCategory,
    getCategoryName,
    getAllCategories,
    getUnitFullName
  };
}